'use client';

import React, {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useState,
  useSyncExternalStore,
} from 'react';
import { useLandingConfiguration } from '@/api/useLandingConfiguration';
import {
  FALLBACK_SIGNUP_MODE,
  isSignupMode,
  resolveSignupCtas,
  type SignupCtas,
  type SignupMode,
} from '@/lib/signupMode';

/**
 * Modalità di registrazione per tutto il sito.
 *
 * L'HTML statico esce dal build con la modalità di ripiego, poi il provider
 * legge `GET /v1/landing/configuration` e le CTA si allineano. Per non far
 * ballare l'etichetta a ogni visita teniamo l'ultima modalità nota in
 * localStorage: al secondo accesso la CTA è già quella giusta al primo frame.
 *
 * Il ripiego non è neutro ed è il punto delicato di questo file: quando non
 * si sa, non si offre la demo. Vedi `FALLBACK_SIGNUP_MODE`.
 */

const STORAGE_KEY = 'tn_signup_mode';

/**
 * Oltre questo tempo le CTA si mostrano comunque, con la modalità di ripiego:
 * `apiClient` non ha una scadenza sua, e una richiesta appesa lascerebbe la
 * pagina senza un bottone per sempre. Meglio una CTA forse sbagliata che
 * nessuna CTA.
 */
const SETTLE_TIMEOUT_MS = 1500;

const listeners = new Set<() => void>();

function readStoredMode(): SignupMode | null {
  try {
    const stored = window.localStorage.getItem(STORAGE_KEY);
    return isSignupMode(stored) ? stored : null;
  } catch {
    // Safari in navigazione privata, storage disabilitato: pazienza
    return null;
  }
}

function writeStoredMode(mode: SignupMode) {
  try {
    if (window.localStorage.getItem(STORAGE_KEY) === mode) return;
    window.localStorage.setItem(STORAGE_KEY, mode);
  } catch {
    return;
  }
  listeners.forEach(listener => listener());
}

function subscribe(listener: () => void) {
  listeners.add(listener);
  window.addEventListener('storage', listener);
  return () => {
    listeners.delete(listener);
    window.removeEventListener('storage', listener);
  };
}

/** Sul server non c'è storage: la prima renderizzazione usa il ripiego. */
const serverSnapshot = () => null;

const SignupCtaContext = createContext<SignupCtas>(resolveSignupCtas(FALLBACK_SIGNUP_MODE));

/**
 * Stato grezzo della modalità. Serve a chi deve prendere una decisione
 * irreversibile — la pagina /demo rimanda alla home se la demo non è attiva —
 * e quindi non può accontentarsi del valore ottimistico dell'HTML statico.
 */
export interface SignupModeState {
  mode: SignupMode;
  /** La configurazione è arrivata dal backend, o la richiesta è fallita */
  isResolved: boolean;
  /** La modalità è una risposta vera — del backend o dell'ultima visita — e non il ripiego */
  isKnown: boolean;
  /**
   * Il backend ha detto la sua, o abbiamo smesso di aspettarlo. Da usare per
   * le decisioni che non si possono ritirare — /demo rimanda alla home —
   * dove `isSettled` non basta, perché quello si accontenta anche del solo
   * ricordo dell'ultima visita.
   */
  isDecided: boolean;
  /**
   * Si può disegnare una CTA senza rischiare di doverla cambiare sotto gli
   * occhi di chi legge: o sappiamo la modalità, o abbiamo smesso di aspettarla.
   * Falso nell'HTML statico, dove per definizione non sappiamo ancora niente.
   */
  isSettled: boolean;
}

const SignupModeContext = createContext<SignupModeState>({
  mode: FALLBACK_SIGNUP_MODE,
  isResolved: false,
  isKnown: false,
  isDecided: false,
  isSettled: false,
});

const SignupModeProvider = ({ children }: { children: React.ReactNode }) => {
  const { data, isLoading } = useLandingConfiguration();
  const storedMode = useSyncExternalStore(subscribe, readStoredMode, serverSnapshot);

  const liveMode = isSignupMode(data?.signup_mode) ? data.signup_mode : null;

  useEffect(() => {
    if (liveMode) writeStoredMode(liveMode);
  }, [liveMode]);

  // La rete non risponde: si smette di aspettare e si mostra il ripiego
  const [gaveUp, setGaveUp] = useState(false);
  useEffect(() => {
    const timer = window.setTimeout(() => setGaveUp(true), SETTLE_TIMEOUT_MS);
    return () => window.clearTimeout(timer);
  }, []);

  const isResolved = !isLoading;
  /**
   * Il backend ha smesso di poter rispondere: o la richiesta è finita senza
   * darci una modalità leggibile (errore, o un valore che non conosciamo), o
   * è appesa da troppo tempo e abbiamo smesso di aspettarla.
   */
  const isDecided = isResolved || gaveUp;

  /* Da qui in poi la modalità -----------------------------------------------
     Finché si aspetta vale il ricordo dell'ultima visita, che è quasi sempre
     giusto e fa trovare la CTA buona al primo fotogramma. Ma appena si sa che
     il backend non risponde il ricordo non conta più e si scende sul ripiego:
     se è giù lui, sono giù anche le chiamate che servono a prenotare la demo,
     quindi proporla vorrebbe dire mandare qualcuno contro un muro. Nel raro
     caso di un ritorno con la demo ricordata e il backend giù la CTA cambia
     una volta, da demo a prova: è il verso giusto in cui sbagliare. */
  const mode =
    liveMode ?? (isDecided ? FALLBACK_SIGNUP_MODE : (storedMode ?? FALLBACK_SIGNUP_MODE));

  const ctas = useMemo(() => resolveSignupCtas(mode), [mode]);
  const isKnown = liveMode !== null || (storedMode !== null && !isDecided);
  const state = useMemo(
    () => ({ mode, isResolved, isKnown, isDecided, isSettled: isKnown || isDecided }),
    [mode, isResolved, isKnown, isDecided]
  );

  return (
    <SignupModeContext.Provider value={state}>
      <SignupCtaContext.Provider value={ctas}>{children}</SignupCtaContext.Provider>
    </SignupModeContext.Provider>
  );
};

/**
 * CTA da mostrare: `primary` (demo quando è attiva), `secondary` imposta dalla
 * modalità, `note` di rassicurazione coerente.
 */
export const useSignupCtas = (): SignupCtas => useContext(SignupCtaContext);

/**
 * Modalità attiva e se è già confermata dal backend. Da usare solo quando la
 * differenza conta davvero: per le CTA basta `useSignupCtas()`.
 */
export const useSignupModeState = (): SignupModeState => useContext(SignupModeContext);

export default SignupModeProvider;
